import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";

import { Project } from "types";
import { TrackingContext } from "context/Tracking";
import { useConfirmation } from "context/Confirmation";
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/solid";
import { DeleteButton } from "components/Button/DeleteButton";
import { PrimaryButton } from "components/Button/PrimaryButton";

import { HazardCard } from "./HazardCard";
import { ReanalyzeModal } from "./ReanalyzeModal";
import { useAnalyzeProject } from "./hooks/useAnalyzeProject";
import { useUpdateAIHazard } from "./hooks/useUpdateAIHazard";
import { ANALYZE_PHASE_KEYS } from "./StartView";
import { GenerationLoadingPanel } from "./GenerationLoadingPanel";
import { useDiscardAIRiskSession } from "./hooks/useDiscardAIRiskSession";
import { SessionProgress, countHazardStatuses } from "./SessionProgress";
import { AIHazardShell, AIHazardStatus, AIRiskSession } from "./types";

interface HazardBoardProps {
  projectId: string;
  project: Project;
  session: AIRiskSession;
}

// BOARD VIEW (plan §1): groups of hazard cards + persistent progress meter +
// reanalyze / add-manually / discard-session actions + completion and
// zero-hazards states. Deliberately NO bulk accept/reject anywhere — accepting
// happens exclusively on the per-hazard detail page (plan §1.2 items 1+2).
export const HazardBoard = ({
  projectId,
  project,
  session,
}: HazardBoardProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { track } = useContext(TrackingContext);
  const { openConfirmation } = useConfirmation();
  const [showReanalyzeModal, setShowReanalyzeModal] = useState(false);
  const hasTrackedCompletion = useRef(false);

  // Collapsible groups: groups with pending hazards start expanded; fully
  // resolved groups start collapsed (state computed once from initial session).
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    session.groups.forEach((group, index) => {
      initial[group.id] = index === 0;
    });
    return initial;
  });

  const analyzeMutation = useAnalyzeProject(projectId);
  const updateHazardMutation = useUpdateAIHazard(projectId);
  const discardSessionMutation = useDiscardAIRiskSession(projectId);

  const { accepted, dismissed, pending, total } = countHazardStatuses(
    session.hazards
  );
  const isComplete = pending === 0 && total > 0;

  useEffect(() => {
    // Fired ONCE when every hazard is resolved (plan §12).
    if (isComplete && !hasTrackedCompletion.current) {
      hasTrackedCompletion.current = true;
      track("ai risk: completed", { accepted, dismissed });
    }
  }, [isComplete, accepted, dismissed, track]);

  const hazardsById = new Map(
    session.hazards.map((hazard) => [hazard.id, hazard])
  );

  const handleDismiss = (hazard: AIHazardShell) => {
    updateHazardMutation.mutate({
      hazardId: hazard.id,
      patch: { status: AIHazardStatus.dismissed },
    });
  };

  // Restore returns to `generated` when a draft was already generated,
  // otherwise back to `suggested` (state machine §1.1).
  const handleRestore = (hazard: AIHazardShell) => {
    updateHazardMutation.mutate({
      hazardId: hazard.id,
      patch: {
        status: hazard.draft
          ? AIHazardStatus.generated
          : AIHazardStatus.suggested,
      },
    });
  };

  const handleReanalyze = (additionalContext: string) => {
    setShowReanalyzeModal(false);

    track("ai risk: start analysis", {
      mode: "reanalyze",
      has_additional_context: !!additionalContext.trim(),
    });

    analyzeMutation.mutate({
      additional_context: additionalContext.trim(),
      mode: "reanalyze",
    });
  };

  const handleDiscardSession = () => {
    openConfirmation(() => discardSessionMutation.mutate(), {
      label: t("risks.ai.board.discard.confirm.label"),
      subline: t("risks.ai.board.discard.confirm.subline"),
    });
  };

  return (
    <div className="mt-6 space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between gap-4">
        <div className="flex-1">
          <SessionProgress hazards={session.hazards} />
        </div>
        <div className="mt-4 sm:mt-0 flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => setShowReanalyzeModal(true)}
            disabled={analyzeMutation.isLoading}
            className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50"
          >
            {t("risks.ai.board.reanalyze.cta")}
          </button>
          <Link
            to={`/projects/${projectId}/risks/create`}
            className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
          >
            {t("risks.ai.board.addManual.cta")}
          </Link>
          <span className="ml-2 pl-2 border-l border-gray-200">
            <DeleteButton
              onClick={handleDiscardSession}
              disabled={discardSessionMutation.isLoading}
            >
              {t("risks.ai.board.discard.cta")}
            </DeleteButton>
          </span>
        </div>
      </div>

      {isComplete && (
        <div className="rounded-lg border border-green-300 bg-green-50 p-4 sm:flex sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium text-green-800">
                {t("risks.ai.board.completion.headline")}
              </p>
              <p className="text-sm text-green-700">
                {t("risks.ai.board.completion.subline")}
              </p>
            </div>
          </div>
          <div className="mt-3 sm:mt-0 flex-shrink-0">
            <PrimaryButton
              testId="ai-risk-completion"
              onClick={() => history.push(`/projects/${projectId}/risks`)}
            >
              {t("risks.ai.board.completion.cta")}
            </PrimaryButton>
          </div>
        </div>
      )}

      {analyzeMutation.isLoading ? (
        <GenerationLoadingPanel phaseKeys={ANALYZE_PHASE_KEYS} />
      ) : total === 0 ? (
        // ZERO-HAZARDS state: the analysis returned nothing → discard the
        // empty session and retry with more context (plan §1).
        <div className="text-center border-2 border-gray-300 border-dashed rounded-lg p-8">
          <h3 className="text-sm font-medium text-gray-900">
            {t("risks.ai.board.empty.headline")}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t("risks.ai.board.empty.subline")}
          </p>
          <div className="mt-6">
            <PrimaryButton
              testId="ai-risk-empty-discard"
              onClick={() => discardSessionMutation.mutate()}
              disabled={discardSessionMutation.isLoading}
            >
              {t("risks.ai.board.empty.cta")}
            </PrimaryButton>
          </div>
        </div>
      ) : (
        session.groups.map((group) => {
          const groupHazards = group.hazard_ids
            .map((hazardId) => hazardsById.get(hazardId))
            .filter((hazard): hazard is AIHazardShell => !!hazard);
          const groupCounts = countHazardStatuses(groupHazards);

          return (
            <section key={group.id}>
              <button
                type="button"
                onClick={() =>
                  setOpenGroups((prev) => ({
                    ...prev,
                    [group.id]: !prev[group.id],
                  }))
                }
                className="w-full flex items-center justify-between gap-4 pb-2 border-b border-gray-200 text-left focus:outline-none"
              >
                <div>
                  <h4 className="text-lg font-medium text-gray-900">
                    {group.label}
                  </h4>
                  <p className="text-sm text-gray-500">{group.description}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <p className="text-xs text-gray-500">
                    {t("risks.ai.board.groupProgress", {
                      resolved: groupCounts.resolved,
                      total: groupCounts.total,
                    })}
                  </p>
                  {openGroups[group.id] ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  )}
                </div>
              </button>
              {openGroups[group.id] && (
                <div className="mt-3 space-y-3">
                  {groupHazards.map((hazard, index) => (
                    <HazardCard
                      key={hazard.id}
                      index={index + 1}
                      hazard={hazard}
                      projectId={projectId}
                      acceptedRiskExists={
                        !!hazard.accepted_risk_resource_id &&
                        !!project.risks?.some(
                          (risk) =>
                            risk.resource_id === hazard.accepted_risk_resource_id
                        )
                      }
                      onDismiss={handleDismiss}
                      onRestore={handleRestore}
                    />
                  ))}
                </div>
              )}
            </section>
          );
        })
      )}

      <ReanalyzeModal
        show={showReanalyzeModal}
        defaultContext={session.additional_context}
        onClose={() => setShowReanalyzeModal(false)}
        onConfirm={handleReanalyze}
      />
    </div>
  );
};
